import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CATEGORY_META = {
  travel:    { label: "Travel",    icon: "🚗", color: "#11a8a4", bg: "#e3f6f5" },
  food:      { label: "Food",      icon: "🍽️", color: "#f59e0b", bg: "#fef3c7" },
  stay:      { label: "Stay",      icon: "🏨", color: "#8b5cf6", bg: "#ede9fe" },
  other:     { label: "Other",     icon: "📦", color: "#6b7280", bg: "#f3f4f6" },
  incentive: { label: "Incentive", icon: "🏆", color: "#91d537", bg: "#f0fce8" },
};

function TechnicianExpenseDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const campId = location.state?.campId;
  const token = localStorage.getItem("token");

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [activeCard, setActiveCard] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    km: "",
    description: "",
    attachment: null,
  });
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const authHeaders = { Authorization: `Token ${token}` };

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token]);

  // ── Load config ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!campId || campId === "undefined" || campId === "null") {
      setConfigError("No camp selected. Please go back and try again.");
      setConfigLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        setConfigLoading(true);
        const technicianId = localStorage.getItem("technicianId");
        const res = await fetch(
          `https://cbackend.xraidigital.com/api/technician/technician-camp-expense-config/?camp=${campId}&technician=${technicianId}`,
          { headers: authHeaders }
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        const configData = Array.isArray(data) ? data[0] : data;
        setConfig(
          configData || {
            food_allowed: false,
            stay_allowed: false,
            incentive_allowed: false,
            other_allowed: false,
            travel_allowed: false,
          }
        );
      } catch (err) {
        setConfigError(err.message || "Failed to load expense configuration.");
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [campId]);

  // ── Load history ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!campId) return;
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const technicianId = localStorage.getItem("technicianId");
        const res = await fetch(
          `https://cbackend.xraidigital.com/api/technician/technician-expenses/?camp=${campId}&technician=${technicianId}`,
          { headers: authHeaders }
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        setHistory(Array.isArray(data) ? data : data.results || []);
      } catch {
        setHistoryError("Could not load expense history.");
      } finally {
        setHistoryLoading(false);
      }
    };
    loadHistory();
  }, [campId, submitSuccess]);

  // ── KM → auto-calculate amount ───────────────────────────────────────────
  const isKmTravel =
    activeCard === "travel" &&
    config?.travel_allowed &&
    config?.km_travel_allowed &&
    !config?.travel_fixed;

  useEffect(() => {
    if (!isKmTravel) return;
    const km = Number(form.km || 0);
    const rate = Number(config?.per_km_rate || 0);
    setForm((prev) => ({ ...prev, amount: (km * rate).toFixed(2) }));
  }, [form.km, isKmTravel, config?.per_km_rate]);

  // ── Derived flags ────────────────────────────────────────────────────────
  const isFixedCategory = (key) => {
    if (!config) return false;
    return (
      (key === "food"   && config.food_fixed)   ||
      (key === "stay"   && config.stay_fixed)   ||
      (key === "travel" && config.travel_fixed) ||
      (key === "other"  && config.other_fixed)
    );
  };

  const fixedAmountFor = (key) => {
    if (!config) return "";
    if (key === "food")   return config.food_amount;
    if (key === "stay")   return config.stay_amount;
    if (key === "travel") return config.travel_fixed_amount;
    if (key === "other")  return config.other_amount;
    return "";
  };

  const isAmountReadonly =
    isFixedCategory(activeCard) || isKmTravel;

  // ── Card list ────────────────────────────────────────────────────────────
  const visibleCards = () => {
    if (!config) return [];
    const cards = [];
    if (config.food_allowed)    cards.push({ key: "food",    fixed: config.food_fixed,    amount: config.food_amount });
    if (config.stay_allowed)    cards.push({ key: "stay",    fixed: config.stay_fixed,    amount: config.stay_amount });
    if (config.travel_allowed)  cards.push({ key: "travel",  fixed: config.travel_fixed,  amount: config.travel_fixed_amount });
    if (config.other_allowed)   cards.push({ key: "other",   fixed: config.other_fixed,   amount: config.other_amount });
    return cards;
  };

  // ── Card click ───────────────────────────────────────────────────────────
  const handleCardClick = (key) => {
    if (activeCard === key) {
      setActiveCard(null);
      setForm({ amount: "", km: "", description: "", attachment: null });
      return;
    }

    setActiveCard(key);
    setForm({
      amount: isFixedCategory(key) ? fixedAmountFor(key) : "",
      km: "",
      description: "",
      attachment: null,
    });
    setFormError(null);
    setSubmitSuccess(false);
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) {
      setFormError("Please enter a valid amount.");
      return;
    }
    if (!form.description.trim()) {
      setFormError("Description is required.");
      return;
    }
    if (isKmTravel && (!form.km || Number(form.km) <= 0)) {
      setFormError("Please enter a valid distance in KM.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      const technicianId = localStorage.getItem("technicianId");

      const payload = new FormData();
      payload.append("camp", campId);
      payload.append("technician", technicianId);
      payload.append("category", activeCard.toUpperCase());
      payload.append("amount", form.amount);
      payload.append("description", form.description);
      if (form.attachment) payload.append("attachment", form.attachment);
      if (activeCard === "travel") {

        // KM Travel
        if (config?.km_travel_allowed) {

          payload.append("travel_type", "KM");
          payload.append("distance_km", form.km);

        }

        // Public Transport
        else if (config?.public_transport_allowed) {

          payload.append("travel_type", "PUBLIC");

        }
      }

      const res = await fetch(
        "https://cbackend.xraidigital.com/api/technician/technician-expenses/",
        {
          method: "POST",
          headers: { Authorization: `Token ${token}` },
          body: payload,
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.detail || `Error ${res.status}`);
      }

      setSubmitSuccess(true);
      setForm({ amount: "", km: "", description: "", attachment: null });
      setActiveCard(null);
    } catch (err) {
      setFormError(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return "—"; }
  };

  // ── Loading / Error screens ──────────────────────────────────────────────
  if (configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: "#11a8a4" }}
          />
          <p className="text-gray-500">Loading expense configuration…</p>
        </div>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md w-full mx-4 text-center">
          <p className="text-red-700 font-semibold mb-2">Something went wrong</p>
          <p className="text-red-500 text-sm mb-4">{configError}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2 text-white rounded-lg text-sm"
            style={{ background: "#11a8a4" }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const cards = visibleCards();
  const meta = CATEGORY_META;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <div className="h-5 w-px bg-gray-200" />
            <h1 className="text-lg font-semibold text-gray-900">Expense Dashboard</h1>
          </div>
          <span
            className="px-3 py-1 rounded-full text-xs font-medium text-white"
            style={{ background: "#11a8a4" }}
          >
            Camp #{campId}
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* ── Success toast ──────────────────────────────────────────── */}
        {submitSuccess && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-3">
            <span className="text-green-600 text-lg">✓</span>
            <p className="text-green-700 text-sm font-medium">Expense submitted successfully.</p>
          </div>
        )}

        {/* ── Incentive read-only banner ──────────────────────────────── */}
        {config.incentive_allowed && (
          <div
            className="rounded-xl p-5 flex items-start gap-4 border"
            style={{ background: meta.incentive.bg, borderColor: "#c6f08b" }}
          >
            <span className="text-3xl mt-0.5">🏆</span>
            <div>
              <p className="font-semibold text-gray-800">Incentive Approved</p>
              <p className="text-sm text-gray-600 mt-1">
                Fixed Incentive: ₹{config.incentive_amount}
              </p>
            </div>
          </div>
        )}

        {/* ── Expense Cards ───────────────────────────────────────────── */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
            Submit an Expense
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {cards.map(({ key, fixed, amount }) => {
              const m = meta[key];
              const isActive = activeCard === key;
              return (
                <button
                  key={key}
                  onClick={() => handleCardClick(key)}
                  className="rounded-xl p-5 text-left transition-all border-2 focus:outline-none"
                  style={{
                    background: isActive ? m.color : m.bg,
                    borderColor: isActive ? m.color : "transparent",
                    boxShadow: isActive ? `0 0 0 3px ${m.color}33` : undefined,
                  }}
                >
                  <span className="text-3xl block mb-2">{m.icon}</span>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: isActive ? "#fff" : m.color }}
                  >
                    {m.label}
                  </span>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: isActive ? "rgba(255,255,255,0.8)" : "#6b7280" }}
                  >
                    {isActive ? "Tap to close" : "Tap to add"}
                  </p>
                  {fixed && (
                    <div
                      className="mt-2 text-xs font-semibold"
                      style={{ color: isActive ? "#fff" : "#15803d" }}
                    >
                      Fixed ₹{amount}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Expense Form ────────────────────────────────────────────── */}
        {activeCard && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

            {/* Form header */}
            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ background: meta[activeCard].bg }}
            >
              <span className="text-2xl">{meta[activeCard].icon}</span>
              <div>
                <h3 className="font-semibold text-gray-800">
                  Submit {meta[activeCard].label} Expense
                </h3>
                {isFixedCategory(activeCard) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Amount fixed by admin — add a description and submit
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-6 space-y-5">

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {formError}
                </div>
              )}

              {/* Fixed-amount info banner */}
              {isFixedCategory(activeCard) && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-4">
                  <div>
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                      Admin-fixed amount
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      ₹{fixedAmountFor(activeCard)}
                    </p>
                  </div>
                </div>
              )}

              {/* KM input — only for KM travel */}
              {isKmTravel && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    Distance (KM) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.km}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, km: e.target.value }))
                    }
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5"
                    placeholder="Enter KM travelled"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Rate: ₹{config?.per_km_rate}/KM
                  </p>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.amount}
                  readOnly={isAmountReadonly}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className={`w-full border rounded-lg px-4 py-2.5 ${
                    isAmountReadonly
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                      : "border-gray-200"
                  }`}
                />
                {isKmTravel && (
                  <p className="text-xs text-gray-400 mt-1">
                    Auto-calculated from distance × rate
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5"
                  placeholder="Add details about this expense…"
                />
              </div>

              {/* Attachment */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  Attachment
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      attachment: e.target.files?.[0] || null,
                    }))
                  }
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity"
                  style={{ background: "#11a8a4", opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? "Submitting…" : "Submit Expense"}
                </button>
                <button
                  onClick={() => {
                    setActiveCard(null);
                    setForm({ amount: "", km: "", description: "", attachment: null });
                    setFormError(null);
                  }}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100"
                >
                  Cancel
                </button>
              </div>

            </div>
          </section>
        )}

        {/* ── History Table ───────────────────────────────────────────── */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
            Expense History
          </h2>

          {historyLoading && (
            <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-6 py-8 justify-center">
              <div
                className="animate-spin rounded-full h-5 w-5 border-b-2"
                style={{ borderColor: "#11a8a4" }}
              />
              <p className="text-sm text-gray-500">Loading history…</p>
            </div>
          )}

          {historyError && !historyLoading && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-4 text-sm text-red-600">
              {historyError}
            </div>
          )}

          {!historyLoading && !historyError && history.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 px-6 py-10 text-center">
              <p className="text-gray-400 text-sm">No expenses submitted yet for this camp.</p>
            </div>
          )}

          {!historyLoading && history.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {["Category", "Amount", "Description", "Date"].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((row, i) => {
                      const cat = (row.category || "").toLowerCase();
                      const m = meta[cat] || {
                        label: row.category, icon: "💸", color: "#6b7280", bg: "#f3f4f6",
                      };
                      return (
                        <tr key={row.id || i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                              style={{ background: m.bg, color: m.color }}
                            >
                              {m.icon} {m.label}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 font-semibold text-gray-800">
                            ₹{Number(row.amount).toLocaleString("en-IN")}
                          </td>
                          <td className="px-5 py-3.5 text-gray-600 max-w-xs truncate">
                            {row.description || "—"}
                          </td>
                          <td className="px-5 py-3.5 text-gray-500">
                            {formatDate(row.created_at || row.date)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-100">
                {history.map((row, i) => {
                  const cat = (row.category || "").toLowerCase();
                  const m = meta[cat] || {
                    label: row.category, icon: "💸", color: "#6b7280", bg: "#f3f4f6",
                  };
                  return (
                    <div key={row.id || i} className="px-5 py-4 space-y-1">
                      <div className="flex justify-between items-center">
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{ background: m.bg, color: m.color }}
                        >
                          {m.icon} {m.label}
                        </span>
                        <span className="font-semibold text-gray-800">
                          ₹{Number(row.amount).toLocaleString("en-IN")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{row.description || "—"}</p>
                      <p className="text-xs text-gray-400">
                        {formatDate(row.created_at || row.date)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Summary footer */}
              <div
                className="px-5 py-3.5 border-t border-gray-200 flex justify-between items-center"
                style={{ background: "#f9fafb" }}
              >
                <span className="text-xs text-gray-500 font-medium">
                  {history.length} expense{history.length !== 1 ? "s" : ""} total
                </span>
                <span className="text-sm font-semibold" style={{ color: "#11a8a4" }}>
                  Total: ₹
                  {history
                    .reduce((sum, r) => sum + Number(r.amount || 0), 0)
                    .toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default TechnicianExpenseDashboard;